import usePageMeta from '../hooks/usePageMeta';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, ShoppingBag, ChevronLeft, ShieldCheck, CheckCircle2 } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { createOrder } from '../api';
import { inr } from '../lib/currency';
import { toast } from 'sonner';

const inputCls = 'w-full bg-white border border-gold/30 px-4 py-3 text-sm outline-none focus:border-gold transition-colors';

export default function Checkout() {
  usePageMeta('Checkout | Hitera', 'Secure checkout — Hitera luxury designer wear.', true);
  const { items, cartTotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [placing, setPlacing] = useState(false);
  const [done, setDone] = useState(null); // holds placed order
  const [f, setF] = useState({ firstName: '', lastName: '', email: '', phone: '', address: '', city: '', zipCode: '', country: '' });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const required = ['firstName', 'lastName', 'email', 'phone', 'address', 'city', 'zipCode', 'country'];

  const placeOrder = async (e) => {
    e.preventDefault();
    for (const k of required) {
      if (!f[k].trim()) { toast.error('Please complete all delivery details'); return; }
    }
    setPlacing(true);
    try {
      const order = await createOrder({
        customer: `${f.firstName} ${f.lastName}`.trim(),
        email: f.email,
        phone: f.phone,
        address: f.address,
        city: f.city,
        zipCode: f.zipCode,
        country: f.country,
        items: items.map((i) => ({ id: i.id, name: i.name, price: i.price, qty: i.qty, size: i.size })),
        total: cartTotal,
      });
      clearCart();
      setDone(order);
    } catch {
      toast.error('Could not place order. Please try again.');
    } finally {
      setPlacing(false);
    }
  };

  // ------ Confirmation ------
  if (done) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center px-6 text-center py-24">
        <CheckCircle2 size={56} className="text-gold-dark mb-6" strokeWidth={1.3} />
        <p className="text-gold-dark tracking-luxe text-xs uppercase mb-3">Order Confirmed</p>
        <h1 className="font-display text-4xl md:text-5xl text-burgundy-900 mb-3">Thank You, {done.customer.split(' ')[0]}</h1>
        <div className="gold-divider" />
        <p className="text-burgundy-900/70 text-sm max-w-md mb-2">Your order <span className="text-burgundy-900 font-medium">{done.id}</span> has been received.</p>
        <p className="text-burgundy-900/60 text-sm max-w-md mb-8">A confirmation has been sent to {done.email}. Our atelier will begin crafting your pieces with care.</p>
        <div className="flex gap-3">
          <Link to="/shop" className="bg-burgundy-900 text-cream px-8 py-3.5 text-xs tracking-wideluxe uppercase hover:bg-burgundy-800 transition-colors">Continue Shopping</Link>
          <Link to="/" className="border border-burgundy-900/25 text-burgundy-900 px-8 py-3.5 text-xs tracking-wideluxe uppercase hover:border-gold transition-colors">Return Home</Link>
        </div>
      </div>
    );
  }

  // ------ Empty guard ------
  if (items.length === 0) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-6 text-center py-24">
        <ShoppingBag size={48} className="text-gold-dark mb-6" strokeWidth={1} />
        <h1 className="font-display text-4xl text-burgundy-900 mb-3">Your Bag Is Empty</h1>
        <p className="text-burgundy-900/60 mb-8 text-sm">Add pieces to your bag before checking out.</p>
        <Link to="/shop" className="bg-burgundy-900 text-cream px-9 py-3.5 text-xs tracking-wideluxe uppercase hover:bg-burgundy-800 transition-colors">Explore The Collection</Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1300px] mx-auto px-4 md:px-8 py-14">
      <button onClick={() => navigate('/cart')} className="flex items-center gap-2 text-xs tracking-wideluxe uppercase text-burgundy-900/60 hover:text-gold-dark mb-6"><ChevronLeft size={15} /> Back to Bag</button>
      <h1 className="font-display text-4xl md:text-5xl text-burgundy-900 mb-2">Checkout</h1>
      <p className="text-burgundy-900/60 text-sm mb-10">Enter your delivery details to complete your order</p>

      <div className="grid lg:grid-cols-3 gap-12">
        {/* Form */}
        <form onSubmit={placeOrder} className="lg:col-span-2 space-y-8">
          <div>
            <h2 className="font-display text-2xl text-burgundy-900 mb-5">Contact Details</h2>
            <div className="grid md:grid-cols-2 gap-5">
              <div><label className="block text-xs tracking-wideluxe uppercase text-burgundy-900/70 mb-2">First Name *</label><input value={f.firstName} onChange={set('firstName')} className={inputCls} /></div>
              <div><label className="block text-xs tracking-wideluxe uppercase text-burgundy-900/70 mb-2">Last Name *</label><input value={f.lastName} onChange={set('lastName')} className={inputCls} /></div>
              <div><label className="block text-xs tracking-wideluxe uppercase text-burgundy-900/70 mb-2">Email *</label><input type="email" value={f.email} onChange={set('email')} className={inputCls} /></div>
              <div><label className="block text-xs tracking-wideluxe uppercase text-burgundy-900/70 mb-2">Phone *</label><input value={f.phone} onChange={set('phone')} className={inputCls} /></div>
            </div>
          </div>

          <div>
            <h2 className="font-display text-2xl text-burgundy-900 mb-5">Delivery Address</h2>
            <div className="grid md:grid-cols-2 gap-5">
              <div className="md:col-span-2"><label className="block text-xs tracking-wideluxe uppercase text-burgundy-900/70 mb-2">Street Address *</label><input value={f.address} onChange={set('address')} className={inputCls} /></div>
              <div><label className="block text-xs tracking-wideluxe uppercase text-burgundy-900/70 mb-2">City *</label><input value={f.city} onChange={set('city')} className={inputCls} /></div>
              <div><label className="block text-xs tracking-wideluxe uppercase text-burgundy-900/70 mb-2">Postal / ZIP Code *</label><input value={f.zipCode} onChange={set('zipCode')} className={inputCls} /></div>
              <div className="md:col-span-2"><label className="block text-xs tracking-wideluxe uppercase text-burgundy-900/70 mb-2">Country *</label><input value={f.country} onChange={set('country')} className={inputCls} /></div>
            </div>
          </div>

          <div className="flex items-center gap-2 text-xs text-burgundy-900/50"><ShieldCheck size={16} className="text-gold-dark" /> Your details are kept private and secure.</div>

          <button type="submit" disabled={placing} className="w-full md:w-auto bg-burgundy-900 text-cream px-12 py-4 text-xs tracking-wideluxe uppercase hover:bg-burgundy-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {placing ? 'Placing Order...' : 'Place Order'} <ArrowRight size={15} />
          </button>
        </form>

        {/* Summary */}
        <div className="lg:col-span-1">
          <div className="bg-cream/60 p-7 sticky top-32">
            <h2 className="font-display text-2xl text-burgundy-900 mb-6">Your Order</h2>
            <div className="space-y-4 max-h-72 overflow-y-auto pr-1">
              {items.map((it) => (
                <div key={it.key} className="flex gap-3">
                  <img src={it.image} alt={it.name} className="w-14 h-18 object-cover bg-white" />
                  <div className="flex-1">
                    <p className="text-sm text-burgundy-900 leading-tight">{it.name}</p>
                    <p className="text-xs text-burgundy-900/50">Size {it.size} · Qty {it.qty}</p>
                  </div>
                  <span className="text-sm text-burgundy-800">{inr(it.price * it.qty)}</span>
                </div>
              ))}
            </div>
            <div className="space-y-3 py-5 mt-4 border-y border-gold/20 text-sm">
              <div className="flex justify-between text-burgundy-900/70"><span>Subtotal</span><span>{inr(cartTotal)}</span></div>
              <div className="flex justify-between text-burgundy-900/70"><span>Shipping</span><span className="text-gold-dark">Complimentary</span></div>
            </div>
            <div className="flex justify-between items-center py-4">
              <span className="text-burgundy-900 tracking-wideluxe uppercase text-sm">Total</span>
              <span className="font-display text-2xl text-burgundy-900">{inr(cartTotal)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
